import { useQuery, type QueryClient } from '@tanstack/react-query';
import { chatApi } from '@/services/chatApi';

/**
 * The family's threads, newest first.
 *
 * ONE query key for both readers (2026-09-21): the archive in Settings ›
 * Companion and the history dialog on Today. Opening the dialog after
 * visiting the archive shows the cached list at once, and the two can never
 * show a different set of threads at the same moment.
 *
 * `staleTime: 0` (#2389): the app-wide default is 5 minutes (main.tsx), so a
 * thread started on /chat stayed missing from the history dialog until a full
 * reload. Each reader now revalidates when it mounts. The cached list renders
 * first, so there is no spinner, only a quiet refetch.
 *
 * `retry: false` so a failure surfaces as the error state rather than
 * spinning — the tri-state (AP-58) depends on the failure being visible.
 */
export const CONVERSATIONS_QUERY_KEY = ['conversations', 'all'] as const;

/** This hook's list and the chat page's own thread query start with this key. */
export const CONVERSATIONS_ROOT_KEY = ['conversations'] as const;

/**
 * The Recents drawer on /chat (chat-core HamburgerDrawer) reads the same
 * threads under its own key, through the chat config's list endpoint. It must
 * stay in step with the key in HamburgerDrawer.tsx; ChatPage.threadList.test
 * renders the real drawer and fails if the two drift.
 */
export const RECENTS_DRAWER_QUERY_KEY = ['conversationList'] as const;

export function useConversations() {
  return useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: () => chatApi.getConversations({ limit: 100 }),
    staleTime: 0,
    retry: false,
  });
}

/**
 * Mark every conversations list stale after a write that adds or changes a
 * thread (#2389): the history dialog and archive, the chat page's thread
 * query, and the Recents drawer on /chat. Mounted readers refetch now;
 * unmounted ones refetch when they next mount.
 */
export function invalidateConversationLists(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_ROOT_KEY }),
    queryClient.invalidateQueries({ queryKey: RECENTS_DRAWER_QUERY_KEY }),
  ]);
}
